import React, { useCallback, useMemo, memo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
} from 'react-native';
import { UnifiedModal } from '@/components/common/UnifiedModal';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { SVG_COMPONENTS } from '@/assets/admin';

interface AdminDialogModalProps {
  visible: boolean;
  title: string;
  onClose: () => void;
  onSave: () => void;
  isSaving: boolean;
  saveText?: string;
  savingText?: string;
  maxWidth?: number;
  children: React.ReactNode;
}

export const AdminDialogModal: React.FC<AdminDialogModalProps> = memo(
  ({
    visible,
    title,
    onClose,
    onSave,
    isSaving,
    saveText = 'Save',
    savingText = 'Saving...',
    maxWidth = 480,
    children,
  }) => {
    const styles = useStyles();
    const { redesign } = useAdminColors();

    const handleClose = useCallback(() => {
      if (!isSaving) onClose();
    }, [isSaving, onClose]);

    const contentContainerStyle = useMemo(
      () => ({ backgroundColor: redesign.bg.modal }),
      [redesign.bg.modal],
    );

    const footer = (
      <View style={styles.footerRow}>
        <TouchableOpacity
          style={styles.cancelButton}
          onPress={onClose}
          disabled={isSaving}
        >
          <Text style={styles.cancelButtonText}>Cancel</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.saveButton, isSaving && styles.saveButtonDisabled]}
          onPress={onSave}
          disabled={isSaving}
        >
          <Text style={styles.saveButtonText}>
            {isSaving ? savingText : saveText}
          </Text>
        </TouchableOpacity>
      </View>
    );

    return (
      <UnifiedModal
        visible={visible}
        onClose={handleClose}
        variant="sheet"
        showHeader={false}
        avoidKeyboard
        dismissOnBackdrop={!isSaving}
        enableGestureDismiss={false}
        onRequestClose={isSaving ? undefined : onClose}
        dialogMaxWidth={maxWidth}
        contentContainerStyle={contentContainerStyle}
        footer={footer}
        footerStyle={{ backgroundColor: redesign.bg.modal }}
      >
        <View style={styles.header}>
          <Text style={styles.headerTitle}>{title}</Text>
          <TouchableOpacity
            onPress={handleClose}
            disabled={isSaving}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <View style={isSaving ? styles.closeButtonDisabled : undefined}>
              <SVG_COMPONENTS.CANCEL_X
                width={18}
                height={18}
                color={redesign.text.dim}
              />
            </View>
          </TouchableOpacity>
        </View>

        <ScrollView
          style={styles.body}
          contentContainerStyle={styles.bodyContent}
          keyboardShouldPersistTaps="handled"
        >
          {children}
        </ScrollView>
      </UnifiedModal>
    );
  },
);

AdminDialogModal.displayName = 'AdminDialogModal';

const useStyles = () =>
  useThemedStyles(({ components }) => {
    const admin = components.admin;
    return StyleSheet.create({
      header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 16,
        borderBottomWidth: 1,
        borderBottomColor: admin.dataTable.border,
      },
      headerTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: admin.dataTable.text,
      },
      closeButtonDisabled: {
        opacity: 0.3,
      },
      body: {
        flexShrink: 1,
      },
      bodyContent: {
        padding: 20,
        gap: 20,
      },
      footerRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 12,
      },
      cancelButton: {
        paddingHorizontal: 20,
        paddingVertical: 10,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: admin.dataTable.border,
      },
      cancelButtonText: {
        fontSize: 14,
        fontWeight: '500',
        color: admin.dataTable.text,
      },
      saveButton: {
        paddingHorizontal: 20,
        paddingVertical: 10,
        borderRadius: 8,
        backgroundColor: admin.formInput.borderFocused,
      },
      saveButtonDisabled: {
        opacity: 0.5,
      },
      saveButtonText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#FFFFFF',
      },
    });
  });
